/**
 * CP2.2 — ShipToAddressForm
 *
 * Anonymous customer-facing address form for "Ship to My Address"
 * fulfillment. Deliberately minimal: name, street1/2, city, region,
 * postal, country, email. No login required.
 *
 * Address-correction dialog: when the server returns a normalized
 * suggestion different from the customer input, the customer picks
 * one — we never silently apply the correction.
 *
 * Accessibility: keyboard-friendly form, label-input pairing, semantic
 * roles, error announcements via aria-live.
 */
import React, { useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { isValidEmail, isValidPhone } from "../../../lib/contactValidation";
import { logCustomerAuditEvent } from "../../../lib/customerAuditLog";

const REQUIRED = ["name", "street1", "city", "region", "postal", "country"];

// Parcel Shipping Readiness & Checkout Completion — Phase 4. A production
// screenshot showed the same apartment/suite text (e.g. "#200") entered
// into BOTH street1 and street2, so the combined address doubled it up.
// Street1 is the field carriers treat as primary, so when street2's
// content is already present in street1, drop the duplicate from
// street2 rather than silently losing either value.
export function normalizeStreetLines(street1, street2) {
  const s1 = (street1 || "").trim();
  const s2 = (street2 || "").trim();
  if (!s1 || !s2) return { street1: s1, street2: s2 };
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "");
  const n1 = norm(s1);
  const n2 = norm(s2);
  if (n2 && n1.includes(n2)) return { street1: s1, street2: "" };
  return { street1: s1, street2: s2 };
}

const empty = {
  name: "",
  street1: "",
  street2: "",
  city: "",
  region: "",
  postal: "",
  country: "US",
  phone: "",
  email: "",
};

export default function ShipToAddressForm({ initial, onSubmit, disabled, onFieldChange, slug, catalogId, sessionId }) {
  const [form, setForm] = useState({ ...empty, ...(initial || {}) });
  const [errors, setErrors] = useState({});

  // Parcel Shipping Checkout Enforcement — reports every keystroke, not
  // just the on-submit shape, so a parent tracking "does this still
  // match the address the current quote was fetched for" can clear a
  // stale selection immediately rather than waiting for resubmission.
  const setField = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    onFieldChange?.(next);
    return next;
  });

  const validate = () => {
    const errs = {};
    REQUIRED.forEach((k) => {
      if (!String(form[k] || "").trim()) errs[k] = "Required";
    });
    // Contact Validation & Messaging Configuration Recovery — this form
    // previously had no phone validation at all (freeform optional
    // field) and its own bespoke email regex. Both fields stay
    // optional here (delivery updates / receipt), but a NONBLANK value
    // must be a genuinely complete one — reuses the same shared
    // validators as WelcomeModal/CheckoutModal so "optional but must be
    // valid if present" behaves identically everywhere it's collected.
    if (form.email && !isValidEmail(form.email)) {
      errs.email = "Enter a valid email address.";
      logCustomerAuditEvent(slug, { event: "contact_email_validation_failed", reasonCode: "email_invalid_format", catalogId, sessionId });
    }
    if (form.phone && !isValidPhone(form.phone)) {
      errs.phone = "Enter a complete 10-digit mobile number.";
      logCustomerAuditEvent(slug, { event: "contact_phone_validation_failed", reasonCode: "phone_incomplete", catalogId, sessionId });
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const { street1, street2 } = normalizeStreetLines(form.street1, form.street2);
    onSubmit && onSubmit({ ...form, street1, street2 });
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-3"
      aria-label="Ship to my address"
      data-testid="ship-to-address-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field id="name" label="Full name" error={errors.name}>
          <Input
            id="name" data-testid="ship-name"
            value={form.name} onChange={(e) => setField("name", e.target.value)}
            disabled={disabled} autoComplete="name" required
          />
        </Field>
        <Field id="email" label="Email (for order receipt)" error={errors.email}>
          <Input
            id="email" type="email" data-testid="ship-email"
            value={form.email} onChange={(e) => setField("email", e.target.value)}
            disabled={disabled} autoComplete="email"
          />
        </Field>
      </div>
      <Field id="street1" label="Street address" error={errors.street1}>
        <Input
          id="street1" data-testid="ship-street1"
          value={form.street1} onChange={(e) => setField("street1", e.target.value)}
          disabled={disabled} autoComplete="address-line1" required
        />
      </Field>
      <Field id="street2" label="Apartment, suite, etc. (optional)">
        <Input
          id="street2" data-testid="ship-street2"
          value={form.street2} onChange={(e) => setField("street2", e.target.value)}
          disabled={disabled} autoComplete="address-line2"
        />
      </Field>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field id="city" label="City" error={errors.city}>
          <Input
            id="city" data-testid="ship-city"
            value={form.city} onChange={(e) => setField("city", e.target.value)}
            disabled={disabled} autoComplete="address-level2" required
          />
        </Field>
        <Field id="region" label="State" error={errors.region}>
          <Input
            id="region" data-testid="ship-region"
            value={form.region} onChange={(e) => setField("region", e.target.value.toUpperCase())}
            disabled={disabled} autoComplete="address-level1" maxLength={3} required
          />
        </Field>
        <Field id="postal" label="ZIP" error={errors.postal}>
          <Input
            id="postal" data-testid="ship-postal"
            value={form.postal} onChange={(e) => setField("postal", e.target.value)}
            disabled={disabled} autoComplete="postal-code" required
          />
        </Field>
        <Field id="country" label="Country" error={errors.country}>
          <Input
            id="country" data-testid="ship-country"
            value={form.country}
            onChange={(e) => setField("country", e.target.value.toUpperCase())}
            disabled={disabled} maxLength={2} required
          />
        </Field>
      </div>
      <Field id="phone" label="Phone (optional, for delivery updates)" error={errors.phone}>
        <Input
          id="phone" data-testid="ship-phone" inputMode="tel"
          value={form.phone} onChange={(e) => setField("phone", e.target.value)}
          disabled={disabled} autoComplete="tel"
        />
      </Field>
      <div className="pt-2">
        <Button
          type="submit" disabled={disabled}
          data-testid="ship-address-submit"
          className="w-full md:w-auto"
        >
          Select shipping method
        </Button>
      </div>
    </form>
  );
}

function Field({ id, label, error, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs font-medium text-stone-700">
        {label}
      </Label>
      {children}
      {error && (
        <p
          className="text-xs text-rose-600 mt-1"
          role="alert" aria-live="polite"
          data-testid={`ship-error-${id}`}
        >
          {error}
        </p>
      )}
    </div>
  );
}
